import { DragHandle } from '@creditornot/cb-icons';
import { colors, cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { useDroppable } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React, { CSSProperties, useMemo } from 'react';
import styled from 'styled-components';

import { PickingArea } from 'apis/picker-api/types';
import { usePluralization } from 'common/hooks';
import { Typography as T } from 'common/typography';
import { solidify } from 'modules/menu/solidify';

import { DnD } from '../types';

type Props = {
  area: PickingArea;
  className?: string;
  selected?: boolean;
  canDrag?: boolean;
  onClick?: () => void;
};

type RowAttrs = {
  bgActive: string;
  bgHover: string;
  bgSelected: string;
  bgOver: string;
};

const themeAttrs: { light: RowAttrs; dark: RowAttrs } = {
  light: {
    bgHover: solidify(colors.wolt8, 'light'),
    bgActive: solidify(colors.wolt12, 'light'),
    bgSelected: solidify(colors.wolt8, 'light'),
    // ignore-color
    bgOver: solidify(colors.pepper20, 'light'),
  },
  dark: {
    bgHover: solidify(colors.wolt12, 'dark'),
    bgActive: solidify(colors.wolt16, 'dark'),
    bgSelected: solidify(colors.wolt12, 'dark'),
    // ignore-color
    bgOver: solidify(colors.salt24, 'dark'),
  },
};

const Root = styled.div<{ selected?: boolean; isOver: boolean; isDragging: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 64px;
  padding: 16px;
  background-color: ${({ selected, isOver, theme }) => {
    if (isOver) {
      return themeAttrs[theme.cbThemeMode].bgOver;
    }
    if (selected) {
      return themeAttrs[theme.cbThemeMode].bgSelected;
    }
    return cbSemanticCssVars.surfaceMain;
  }};
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  color: ${cbSemanticCssVars.textPrimary};
  cursor: pointer;
  &:hover {
    background-color: ${({ theme }) => themeAttrs[theme.cbThemeMode].bgHover};
  }

  &:active {
    background-color: ${({ theme }) => themeAttrs[theme.cbThemeMode].bgActive};
  }
  /* dnd-kit is setting opacity of sortable items to zero, so we need to overwrite it */
  opacity: ${({ isDragging }) => (isDragging ? 0.25 : 1)} !important;
`;

const LeftContent = styled.div`
  min-width: 0;
  flex: 1;
  display: flex;
  align-items: center;
`;

const StyledDragHandle = styled(DragHandle)`
  flex-shrink: 0;
  width: 16px;
  fill: ${cbSemanticCssVars.textPrimary};
  margin-inline-end: 16px;
  cursor: grab;
`;

const AreaName = styled(T.Heading6)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Label = styled(T.Small)`
  margin-inline-start: 8px;
  flex-shrink: 0;
`;

const AreaRow = ({ area, className, selected, canDrag, onClick }: Props) => {
  const { FormattedPluralMessage } = usePluralization();
  const droppable = useDroppable({ id: area.id, data: { type: DnD.Area } });
  const sortable = useSortable({ id: area.id, disabled: !canDrag, data: { type: DnD.Area } });

  const isOver = useMemo(() => {
    if (
      selected ||
      (droppable.over?.data.current?.type === DnD.Area && sortable.active?.data.current?.type === DnD.Area)
    ) {
      return false;
    }
    return droppable.isOver;
  }, [droppable.isOver, droppable.over?.data, selected, sortable.active?.data]);

  const style: CSSProperties = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
  };

  return (
    <span ref={droppable.setNodeRef}>
      <Root
        ref={sortable.setNodeRef}
        className={className}
        style={style}
        {...sortable.attributes}
        {...sortable.listeners}
        selected={selected}
        isOver={isOver}
        isDragging={sortable.isDragging}
        onClick={onClick}
      >
        <LeftContent>
          {canDrag && <StyledDragHandle />}
          <AreaName title={area.name}>{area.name}</AreaName>
        </LeftContent>
        <Label>
          <FormattedPluralMessage id="picking-route.item-count" count={area.itemsCount} />
        </Label>
      </Root>
    </span>
  );
};

export default AreaRow;
